import {
  ProgressTrackerGroup,
  ProgressTrackerStages,
  ProgressTrackerStatus,
} from 'models/account';
import { isStageComplete, isStageStatus } from 'helpers/progressTracker';
import { useEffect, useMemo } from 'react';
import { queryClient } from 'states/reactQueryClient';
import { Business } from 'models/business';
import { useInitIncorporationStatus } from 'hooks/api';
import { ENTITY_MAPPING } from 'constants/common';

const finishAllCustomerTasks = (progress: ProgressTrackerGroup[]) => {
  const completeCompanyDetails = isStageComplete(
    ProgressTrackerStages.CompanyDetails,
    progress,
  );
  const completePersonalDetails = isStageComplete(
    ProgressTrackerStages.PersonalDetails,
    progress,
  );
  const completeAddressDetails = isStageComplete(
    ProgressTrackerStages.AddressDetails,
    progress,
  );
  const uploadIdentityProof =
    isStageStatus(
      ProgressTrackerStages.UploadIdentityProof,
      progress,
      ProgressTrackerStatus.Completed,
    ) ||
    isStageStatus(
      ProgressTrackerStages.UploadIdentityProof,
      progress,
      ProgressTrackerStatus.InProgress,
    );
  const uploadLastYearTaxStatement =
    isStageStatus(
      ProgressTrackerStages.UploadLastYearTaxStatement,
      progress,
      ProgressTrackerStatus.Completed,
    ) ||
    isStageStatus(
      ProgressTrackerStages.UploadLastYearTaxStatement,
      progress,
      ProgressTrackerStatus.InProgress,
    ) ||
    isStageStatus(
      ProgressTrackerStages.UploadLastYearTaxStatement,
      progress,
      ProgressTrackerStatus.Rejected,
    );
  const completeHomeAddressDetails = isStageComplete(
    ProgressTrackerStages.HomeAddressDetails,
    progress,
  );
  return (
    completeCompanyDetails &&
    completePersonalDetails &&
    completeAddressDetails &&
    uploadIdentityProof &&
    uploadLastYearTaxStatement &&
    completeHomeAddressDetails
  );
};

export const useAutoInitIncorporationStatus = (
  business: Business | undefined,
  businessProgress: ProgressTrackerGroup[],
) => {
  const { initIncorporationStatusAsync } = useInitIncorporationStatus(
    business?.id ?? '',
  );
  const finishedAll = useMemo(
    () => finishAllCustomerTasks(businessProgress),
    [businessProgress],
  );
  useEffect(() => {
    (async function () {
      if (
        finishedAll &&
        business?.id != null &&
        business?.id !== '' &&
        business?.bibHubspot == null &&
        business?.entityType === ENTITY_MAPPING.sole_prop
      ) {
        await initIncorporationStatusAsync();
        await queryClient.invalidateQueries(['business', business?.id]);
      }
    })();
  }, [
    finishedAll,
    business?.bibHubspot,
    business?.id,
    business?.entityType,
    initIncorporationStatusAsync,
  ]);
};
